import {
  ListObjectsV2Command,
  ListObjectsV2CommandOutput,
  S3Client,
} from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { useCallback, useMemo, useState } from 'react';
import { pdfjs } from 'react-pdf';
import invariant from 'tiny-invariant';
import { CombinedError, useMutation, useQuery } from 'urql';
import awsExports from '../aws-exports';
import {
  CreateSheetDocument,
  GetActiveSheetsForProjectDocument,
  GetMagicSheetShapeDocument,
  SheetsDeepFragment,
} from '../gql/graphql';
import { useUserTenant } from '../services/auth-info';
import { useS3Client } from './s3';

export type Sheet = SheetsDeepFragment & { s3Key: string };

type UseSheetReturn = {
  loading: boolean;
  error: CombinedError | undefined;
  submitting: boolean;
  sheets: Sheet[] | null;
  createSheet: (
    file: File,
    name: string,
    version: number
  ) => Promise<SheetsDeepFragment | null>;
};

const generateS3KeyForSheet = (
  tenant: string,
  projectId: string,
  filename: string,
  version: number
) => `${tenant}/project/${projectId}/sheets/${version}/${filename}`;

export function useSheets(projectId: string): UseSheetReturn {
  const { tenant: userTenant, isProtan } = useUserTenant();
  const tenant = userTenant?.group;
  const s3Client = useS3Client(projectId);
  const [submitting, setSubmitting] = useState(false);
  const [{ data: sheetsData, fetching: fetchingSheets, error: sheetsError }] =
    useQuery({
      query: GetActiveSheetsForProjectDocument,
      variables: {
        projectId,
      },
    });
  const [, executeCreateSheetMutation] = useMutation(CreateSheetDocument);
  const [, getMagicSheetShapesMutation] = useMutation(
    GetMagicSheetShapeDocument
  );

  let sheets = useMemo(() => {
    if (!sheetsData?.project?.activeSheets || !tenant) {
      return null;
    }
    // activeSheets is expected to contain only the latest version per sheet name
    return sheetsData.project.activeSheets.map((sheet) => ({
      ...sheet,
      s3Key: generateS3KeyForSheet(
        tenant,
        sheet.projectId,
        sheet.filename,
        sheet.version
      ),
    }));
  }, [sheetsData?.project?.activeSheets, tenant]);

  const createSheet = useCallback(
    async (file: File, name: string, version: number) => {
      invariant(awsExports.attachments_bucket_name, 'No bucket configured');
      invariant(tenant, 'No tenant configured');
      // Compute a non-colliding S3 key by adjusting filename if needed
      const { finalFilename, key } = await resolveAvailableSheetKey(
        s3Client,
        awsExports.attachments_bucket_name,
        tenant,
        projectId,
        version,
        file.name
      );
      try {
        setSubmitting(true);

        const upload = new Upload({
          client: s3Client,
          params: {
            Bucket: awsExports.attachments_bucket_name,
            Key: key,
            Body: file,
            ContentType: 'application/pdf',
            Metadata: {
              name: encodeURIComponent(name),
              version: version.toString(),
              projectId,
            },
          },
        });
        // TODO: Add progress status subscription here

        await upload.done();
        const { data, error } = await executeCreateSheetMutation({
          input: {
            sheet: {
              projectId,
              name,
              filename: finalFilename,
              version,
            },
          },
        });
        // TODO: Graceful error handling - remove sheet from s3?
        if (error) {
          console.error('Create sheet mutation error', error);
          throw new Error('An unexpected error occurred when creating sheet');
        }

        // Low effort way of triggering embedding lambda for generating SAM embeddings on upload
        // Ideally, this should have been done with S3 notifications on upload,
        // but i ran into circular stack dependencies in CDK and ran out of time
        const generateEmbeddingsForSheet = async () => {
          const createdSheetId = data?.createSheet?.sheet?.id;
          const { numPages } = await pdfjs.getDocument(await file.arrayBuffer())
            .promise;
          if (numPages <= 5 && createdSheetId && isProtan) {
            for (let pageNumber = 0; pageNumber < numPages; pageNumber++) {
              await getMagicSheetShapesMutation({
                input: {
                  sheetId: createdSheetId,
                  pageNumber,
                  mousePoints: [{ foreground: true, point: { x: 0, y: 0 } }],
                },
              });
            }
          }
        };
        generateEmbeddingsForSheet().catch((err) =>
          console.error('Error on generating embeddings for sheet', err)
        );

        return data?.createSheet?.sheet ?? null;
      } finally {
        setSubmitting(false);
      }
    },
    [
      executeCreateSheetMutation,
      getMagicSheetShapesMutation,
      isProtan,
      projectId,
      s3Client,
      tenant,
    ]
  );

  return {
    sheets,
    error: sheetsError,
    loading: fetchingSheets,
    submitting,
    createSheet,
  };
}

async function listExistingFilenames(
  s3Client: S3Client,
  bucketName: string,
  prefix: string
): Promise<Set<string>> {
  const filenames = new Set<string>();
  let continuationToken: string | undefined = undefined;
  do {
    const response: ListObjectsV2CommandOutput = await s3Client.send(
      new ListObjectsV2Command({
        Bucket: bucketName,
        Prefix: prefix,
        ContinuationToken: continuationToken,
      })
    );
    for (const object of response.Contents ?? []) {
      const objectKey = object.Key ?? '';
      if (objectKey.startsWith(prefix)) {
        const fname = objectKey.slice(prefix.length);
        if (fname) filenames.add(fname);
      }
    }
    continuationToken = response.IsTruncated
      ? response.NextContinuationToken
      : undefined;
  } while (continuationToken);
  return filenames;
}

function chooseAvailableFilename(
  existing: Set<string>,
  originalFilename: string
): string {
  if (!existing.has(originalFilename)) return originalFilename;
  const dotIdx = originalFilename.lastIndexOf('.');
  const base =
    dotIdx > 0 ? originalFilename.slice(0, dotIdx) : originalFilename;
  const ext = dotIdx > 0 ? originalFilename.slice(dotIdx) : '';
  let i = 2;
  let candidate = `${base} (${i})${ext}`;
  while (existing.has(candidate)) {
    i += 1;
    candidate = `${base} (${i})${ext}`;
  }
  return candidate;
}

async function resolveAvailableSheetKey(
  s3Client: S3Client,
  bucketName: string,
  tenant: string,
  projectId: string,
  version: number,
  originalFilename: string
): Promise<{ finalFilename: string; key: string }> {
  const prefix = `${tenant}/project/${projectId}/sheets/${version}/`;
  const getKeyForFilename = (fname: string) => `${prefix}${fname}`;
  try {
    // List the directory prefix and filter client-side by base pattern
    const dotIdx = originalFilename.lastIndexOf('.');
    const base =
      dotIdx > 0 ? originalFilename.slice(0, dotIdx) : originalFilename;
    const ext = dotIdx > 0 ? originalFilename.slice(dotIdx) : '';
    const allFilenames = await listExistingFilenames(
      s3Client,
      bucketName,
      prefix
    );
    // Filter to only the same-extension and naming pattern we support
    const regex = new RegExp(
      `^${escapeRegExp(base)}( \\([0-9]+\\))?${escapeRegExp(ext)}$`
    );
    const existing = new Set<string>(
      Array.from(allFilenames).filter((name) => regex.test(name))
    );
    const chosen = chooseAvailableFilename(existing, originalFilename);
    return { finalFilename: chosen, key: getKeyForFilename(chosen) };
  } catch (error: unknown) {
    throw new Error(
      `Error resolving available sheet key: ${(error as Error).message}`
    );
  }
}

function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
